import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "styled-components";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useFormik } from "formik";
import { Lock, HelpCircle } from "lucide-react";
import Toggle from "react-toggle";

import {
  SwitchForm,
  ModalFooterWrapper,
  ModalFooterLeft,
  ModalFooterRight,
} from "../../../sharedStyles";
import { Candidate, LocalityData } from "../../../interfaces";
import { GET_LOCALITIES } from "../../../GraphQL/settings/queries";
import {
  ADD_CANDIDATE,
  AMEND_CANDIDATE,
  IMPORT_CANDIDATE,
} from "../../../GraphQL/candidates/mutations";
import { GET_COMPANY_USERS } from "../../../GraphQL/settings/queries";
import { industries } from "../../../const";
import { useMutation, useQuery } from "@apollo/client";
import confirmWithModal from "../../Global/confirmWithModal";
import DraftEditor from "../../DraftEditor";
import ReactSelect from "react-select";
import AttachmentSection from "../../Attachments/AttachmentSection";
import {
  EToastPosition,
  EToastColorStatus,
  EModalFormType,
} from "../../../interfaces";
import {
  GET_CANDIDATE_BY_ID,
  GET_PROJECT_CANDIDATE_BOARDS,
} from "../../../GraphQL/candidates/queries";

interface Props {
  isNonATS: boolean;
  show: boolean;
  onHide: () => void;
  candidate: Candidate | null;
  reloadCandidates: () => any;
  atsServer: string;
  onCreateToast?: (
    color: EToastColorStatus,
    position: EToastPosition,
    message: string
  ) => void;
  onShowDelete: (data: boolean) => void;
  onSetDeleteId: (data: number) => void;
}

export const LabelNote = styled.span`
  color: #888;
  font-weight: normal;
  font-size: 16px;
`;

const SalaryInputs = styled.div`
  display: flex;
  justify-content: space-between;
  & input {
    width: 100%;
  }
  & select {
    width: 90px;
  }

  gap: 10px;
`;

const CheckWrap = styled.div`
  display: flex;
  flex-direction: row;
`;

const CandidateDetailFormSchema = Yup.object().shape({
  localityId: Yup.string().required("Required"),
  workType: Yup.string().required("Required"),
  summary: Yup.string()
    .test(
      "value",
      "Description is Required",
      (value) => value !== "<p><br></p>"
    )
    .required("Description is Required"),
  industry: Yup.string().required("Required"),
  yearsOfExperience: Yup.number()
    .min(0, "Minimum value should be 0")
    .required("Required and should be a number"),
  idealJobTitles: Yup.string().required("Required"),
  salaryMin: Yup.number()
    .min(40000, "Minimum salary is $40,000")
    .required("Required"),
  salaryMax: Yup.number()
    .required("Required")
    .moreThan(Yup.ref("salaryMin"), "Max salary must be more than min salary"),
});

const newCandidateDetailFormSchema = Yup.object().shape({
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
});

const ToggleContainer = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: left;

  & div {
    margin: 0 4px;
  }
`;

const Hyphen = styled.div`
  padding-top: 5px;
`;

const ProjectCandidateDetailModal = ({
  isNonATS,
  show,
  onHide,
  candidate,
  reloadCandidates,
  atsServer,
  onCreateToast = () => {},
  onShowDelete,
  onSetDeleteId,
}: Props) => {
  const {
    error: errorCompany,
    loading: loadingCompany,
    data: dataCompany,
  } = useQuery(GET_COMPANY_USERS, {
    onCompleted: (data) => setCandidateOwners(data?.companyUsers?.items),
  });

  const {
    error: errorProjectBoards,
    loading: loadingProjectBoards,
    data: dataProjectBoards,
  } = useQuery(GET_PROJECT_CANDIDATE_BOARDS);

// ✅ REPLACE THE useQuery WITH THIS
const shouldSkip = !show || !candidate || !candidate.id;

const { data: candidateByIdData, loading: candidateLoading } = useQuery(GET_CANDIDATE_BY_ID, {
  variables: {
    id: candidate?.id ? Number(candidate.id) : 0,
  },
  skip: shouldSkip,
  fetchPolicy: shouldSkip ? "cache-only" : "network-only",
});

  let initCandidateOwners = [];
  if (candidate?.candidateOwners) {
    for (let i in candidate?.candidateOwners)
      initCandidateOwners.push({
        value: candidate?.candidateOwners[i].userId,
        label: candidate?.candidateOwners[i].email,
      });
  }

  const [candidateOwnersValue, setCandidateOwnersValue] =
    useState(initCandidateOwners);
  const [candidateOwners, setCandidateOwners] = useState<any>([]);
  let ownerOptions = [];
  if (dataCompany)
    ownerOptions = candidateOwners.map((item: any) => ({
      value: item?.id,
      label: item?.email,
    }));

  let initProjectBoards = [];
  if (candidateByIdData) {
    for (let i in candidateByIdData?.candidates?.items[0]?.projectBoards) {
      initProjectBoards.push({
        value: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.id,
        label: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.title,
      });
    }
  }

  const [projectBoardsValue, setProjectBoardsValue] =
    useState(initProjectBoards);
  const [projectBoards, setProjectBoards] = useState<any>([]);

  const [addCandidate] = useMutation(ADD_CANDIDATE);
  const [amendCandidate] = useMutation(AMEND_CANDIDATE);
  const [importCandidate] = useMutation(IMPORT_CANDIDATE);
  const [sharing, setSharing] = useState(false);
  const [atsCandidateId, setAtsCandidateId] = useState<string>("");
  const [modalFormType, setModalFormType] = useState<EModalFormType>(
    EModalFormType.ID
  );
  const { data } = useQuery<LocalityData>(GET_LOCALITIES);

  const localities = data?.localities?.items ? [...data.localities.items] : [];
  localities.sort((a, b) => {
    return a.state.localeCompare(b.state) || a.name.localeCompare(b.name);
  });

  useEffect(() => {
    let ownersValue = [];
    if (candidate?.candidateOwners) {
      for (let i in candidate?.candidateOwners)
        ownersValue.push({
          value: candidate?.candidateOwners[i].userId,
          label: candidate?.candidateOwners[i].email,
        });
    }
    setCandidateOwnersValue(ownersValue);

    let projectBoardValue = [];
    if (candidateByIdData) {
      for (let i in candidateByIdData?.candidates?.items[0]?.projectBoards) {
        projectBoardValue.push({
          value: candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.id,
          label:
            candidateByIdData?.candidates?.items[0]?.projectBoards[i]?.title,
        });
      }
    }
    setProjectBoardsValue(projectBoardValue);

    setSharing(candidate?.status === "SHARED");
  }, [candidate, candidateByIdData]);

  useEffect(() => {
    let val = [];
    if (dataProjectBoards?.projectCandidateBoards?.items) {
      for (let i in dataProjectBoards?.projectCandidateBoards?.items) {
        val.push({
          value: dataProjectBoards?.projectCandidateBoards?.items[i]?.id,
          label: dataProjectBoards?.projectCandidateBoards?.items[i]?.title,
        });
      }
    }
    setProjectBoards(val);
  }, [dataProjectBoards]);

  const onDelete = () => {
    if (candidate?.id) onSetDeleteId(candidate?.id);
    onShowDelete(true);
    onHide();
  };

  const handleATSCandidate = () => {
    if (atsCandidateId) {
      onCreateToast(
        EToastColorStatus.WARNING,
        EToastPosition.BOTTOM_END,
        "Sync requested for candidate: " + atsCandidateId
      );

      importCandidate({
        variables: {
          candidateId: parseInt(atsCandidateId),
        },
      })
        .then(() => {
          reloadCandidates();
          onCreateToast(
            EToastColorStatus.SUCCESS,
            EToastPosition.BOTTOM_END,
            "Candidate imported!"
          );
          onHide();
        })
        .catch(() => {
          onCreateToast(
            EToastColorStatus.FAILED,
            EToastPosition.BOTTOM_END,
            "Error importing candidate. Please check the candidate ID and try again."
          );
          onHide();
        });
    } else {
      onCreateToast(
        EToastColorStatus.WARNING,
        EToastPosition.BOTTOM_END,
        "Please input a candidate ID to sync"
      );
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: candidate
      ? {
          ...candidate,
          localityId: "" + candidate?.location?.localityId,
          share: false,
          summary: candidate?.summary ?? "",
        }
      : {
          firstName: "",
          lastName: "",
          localityId: "",
          summary: "",
          industry: "",
          yearsOfExperience: "",
          idealJobTitles: "",
          salaryMin: "",
          salaryMax: "",
          share: false,
          audience: "PRIVATE",
          interviewed: false,
          location: "",
          workType: "",
          currency: "AUD",
          ownerUserIds: [],
          projectCandidateBoardIds: [],
          dropbox: "",
        },
    validateOnChange: true,
    validationSchema: candidate
      ? CandidateDetailFormSchema
      : CandidateDetailFormSchema.concat(newCandidateDetailFormSchema),
    onSubmit: async (values) => {
      if (values.share) {
        const confirmed = await confirmWithModal(
          <>
            <p>Before sharing a candidate, you confirm that</p>
            <ul>
              <li>
                You have received permission from the Candidate to be posted on
                the platform;
              </li>
              <li>
                there is a genuine opportunity for the Candidate to accept a
                role with a Client; and
              </li>
              <li>
                your agency has interviewed or screened the Candidate within the
                last three months.
              </li>
            </ul>
          </>
        );
        if (!confirmed) return;
      }

      if (candidate) {
        amendCandidate({
          variables: {
            ...values,
            localityId: parseInt(values.localityId),
          },
        })
          .then(() => {
            reloadCandidates();
            onCreateToast(
              EToastColorStatus.SUCCESS,
              EToastPosition.BOTTOM_END,
              "Candidate updated!"
            );
            onHide();
          })
          .catch(() => {
            onCreateToast(
              EToastColorStatus.FAILED,
              EToastPosition.BOTTOM_END,
              "Error while updating candidate, please ensure all fields are filled"
            );
          });
      } else {
        addCandidate({
          variables: {
            ...values,
            middleName: "",
            jobTitle: values.idealJobTitles,
            localityId: parseInt(values.localityId),
            lastName: `[VMS] ${values.lastName}`,
          },
        })
          .then((response) => {
            onCreateToast(
              EToastColorStatus.SUCCESS,
              EToastPosition.BOTTOM_END,
              "Candidate added successfully!"
            );
            reloadCandidates();
            onHide();
          })
          .catch(() => {
            onCreateToast(
              EToastColorStatus.FAILED,
              EToastPosition.BOTTOM_END,
              "Error while updating candidate, please ensure all fields are filled"
            );
          });
      }
    },
  });

  const fv = formik.values;

  const { errors, touched } = formik;

  const ErrorWrapper = ({ field }: { field: keyof typeof errors }) => {
    return (
      <>
        {errors && touched[field] ? (
          <div style={{ color: "red" }}>{errors[field]}</div>
        ) : null}
      </>
    );
  };

  if (!isNonATS && !candidate && modalFormType === EModalFormType.ID) {
    return (
      <Dialog open={show} onOpenChange={onHide}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Project Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Enter the ID of a candidate you would like to add to Role Relay.{" "}
              {/* TODO */}
              {/* <Button variant="link">Where do I find my role ID?</Button> */}
            </p>
            <div className="space-y-2">
              <Label htmlFor="candidateId">Candidate ID*</Label>
              <Input
                type="text"
                id="candidateId"
                name="roleId"
                onChange={(e) => setAtsCandidateId(e.target.value)}
                required
              />
            </div>
            <br />
            <SwitchForm onClick={() => setModalFormType(EModalFormType.MANUAL)}>
              Add candidate manually instead
            </SwitchForm>
          </div>
          <DialogFooter>
            <Button onClick={onHide} variant="secondary">
              Cancel
            </Button>
            <Button variant="default" onClick={handleATSCandidate}>
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={show} onOpenChange={onHide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            {candidate?.firstName ? (
              <DialogTitle>
                {candidate?.firstName}{" "}
                {candidate?.lastName?.replace("[VMS] ", "").slice(0, 1)}.
                {candidate?.jobTitle ? ` - ${candidate.jobTitle}` : ""}
                {candidate?.location?.displayName
                  ? ` - ${candidate.location.displayName}`
                  : ""}
              </DialogTitle>
            ) : (
              <DialogTitle>New Project Candidate</DialogTitle>
            )}
          </DialogHeader>
          <div className="space-y-4">
          {!isNonATS && !candidate && (
            <SwitchForm onClick={() => setModalFormType(EModalFormType.ID)}>
              Import candidate from your ATS instead
            </SwitchForm>
          )}
          <br />
          <form onSubmit={formik.handleSubmit}>
                {/* <Row>
                  <Col>
                    <br />
                    <p>
                      <strong>Note:</strong> These details will be visible to
                      other agencies on the platform when sharing is activated.
                      Please ensure you check all fields (particularly the
                      Description/Profile) for any identifiable information.
                    </p>
                  </Col>
                </Row> */}
                {!candidate && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="lastName">
                          Family name*{" "}
                          <LabelNote>
                            <Lock className="inline h-4 w-4" />
                          </LabelNote>
                        </Label>
                        <Input
                          type="text"
                          id="lastName"
                          name="lastName"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                        />
                        <ErrorWrapper field="lastName" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="firstName">
                          First name*{" "}
                          <LabelNote>
                            <Lock className="inline h-4 w-4" />
                          </LabelNote>
                        </Label>
                        <Input
                          type="text"
                          id="firstName"
                          name="firstName"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                        />
                        <ErrorWrapper field="firstName" />
                      </div>
                    </div>
                    <br />
                  </>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="localityId">Location*</Label>
                        <select
                          id="localityId"
                          name="localityId"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={fv.localityId}
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                        >
                          <option></option>
                          {localities.map((locality) => (
                            <option key={locality.id} value={locality.id}>
                              {locality.name} ({locality.state})
                            </option>
                          ))}
                        </select>
                        <ErrorWrapper field="localityId" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="workType">Type</Label>
                        <select
                          id="workType"
                          name="workType"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={fv.workType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value=""></option>
                          <option value="PERMANENT">PERMANENT</option>
                          <option value="TEMP">CONTRACT/TEMP</option>
                          <option value="ANY">ANY</option>
                        </select>
                        <ErrorWrapper field="workType" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="salaryMin">Salary range (min. $40,000)*</Label>
                    <SalaryInputs>
                      <div>
                        <Input
                          type="number"
                          id="salaryMin"
                          name="salaryMin"
                          placeholder="Min."
                          required
                          value={fv.salaryMin}
                          onChange={formik.handleChange}
                          onBlur={(...args) => {
                            formik.handleBlur(...args);
                            formik.validateField("salaryMax");
                          }}
                        />
                        <ErrorWrapper field="salaryMin" />
                      </div>
                      <Hyphen> - </Hyphen>
                      <div>
                        <Input
                          type="number"
                          id="salaryMax"
                          name="salaryMax"
                          placeholder="Max."
                          value={fv.salaryMax}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          required
                        />
                        <ErrorWrapper field="salaryMax" />
                      </div>
                      <div>
                        <select
                          name="currency"
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={fv.currency}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="AUD">AUD</option>
                          <option value="NZD">NZD</option>
                        </select>
                      </div>
                    </SalaryInputs>
                  </div>
                </div>
                <br />
                <div className="space-y-2">
                  <Label htmlFor="summary">Description/Profile*</Label>
                  <DraftEditor formik={formik} />
                  <ErrorWrapper field="summary" />
                </div>
                <br />
                <div className="space-y-2">
                  <Label htmlFor="dropbox">Shared files (e.g. dropbox) URL</Label>
                  <Input
                    type="text"
                    id="dropbox"
                    name="dropbox"
                    value={fv.dropbox}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    required
                  />
                  {/* <ErrorWrapper field="dropbox" /> */}
                </div>
                <br />
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry*</Label>
                    <select
                      id="industry"
                      name="industry"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={fv.industry}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                    >
                      <option></option>
                      {industries.map((industry) => (
                        <option key={industry} value={industry}>
                          {industry}
                        </option>
                      ))}
                    </select>
                    <ErrorWrapper field="industry" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="yearsOfExperience">Years experience*</Label>
                    <Input
                      type="number"
                      id="yearsOfExperience"
                      name="yearsOfExperience"
                      required
                      value={fv.yearsOfExperience}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorWrapper field="yearsOfExperience" />
                  </div>
                </div>
                <br />
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="idealJobTitles">Ideal job titles*</Label>
                    <Input
                      type="text"
                      id="idealJobTitles"
                      name="idealJobTitles"
                      required
                      value={fv.idealJobTitles}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorWrapper field="idealJobTitles" />
                  </div>
                  {!loadingCompany && !errorCompany && dataCompany && false && (
                    <div className="space-y-2">
                      <Label htmlFor="candidateOwners">Owners</Label>
                      <ReactSelect
                        onChange={(data: any) => {
                          let ownerIds = [];
                          for (let i in data)
                            ownerIds.push(Number(data[i]?.value));
                          formik.setFieldValue("ownerUserIds", ownerIds);
                          setCandidateOwnersValue(data);
                        }}
                        name="candidateOwners"
                        options={ownerOptions}
                        isMulti={true}
                        value={candidateOwnersValue}
                      />
                      <ErrorWrapper field="idealJobTitles" />
                    </div>
                  )}
                </div>
                <br />
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="interviewed"
                    checked={fv.interviewed}
                    onCheckedChange={(checked) =>
                      formik.setFieldValue("interviewed", checked)
                    }
                    name="interviewed"
                  />
                  <Label htmlFor="interviewed" className="text-sm font-normal cursor-pointer">
                    I have received permission from the candidate to be represented on Role Relay in the last 3 months
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 cursor-pointer" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Role Relay's terms dictate that:</p>
                        <ul className="list-disc pl-4">
                          <li>
                            Only candidates that you have engaged with in
                            the last 3 months can be represented on the
                            platform.
                          </li>
                          <li>
                            You must have written permission from the
                            candidate to represent them on the platform.
                          </li>
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <hr />
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Label>Sharing</Label>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="h-4 w-4 cursor-pointer" />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            <strong>Shared for Collaboration</strong> will push
                            this candidate to the Role Relay Network based on
                            your <strong>Audience</strong> selection. Candidates
                            you do not wish to share with the network can be set
                            to <strong>Draft</strong>.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <ToggleContainer>
                    <span>Draft</span>
                    <Toggle
                      name="shared"
                      icons={false}
                      onChange={(evt) => {
                        setSharing(evt.target.checked);
                      }}
                      checked={sharing}
                    />
                    <span>Shared for collaboration</span>
                  </ToggleContainer>
                  {/* <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="audience">Audience</Label>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <HelpCircle className="h-4 w-4 cursor-pointer" />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>
                              Select which audience you would like to share
                              the Candidate with.
                            </p>
                            <ul className="list-disc pl-4">
                              <li>
                                <strong>Private</strong> - only flagged for
                                Role Relay automatic matching. You will
                                receive notifications of opportunities Role
                                Relay detects for this candidate, but their
                                anonymous details will not be searchable for
                                other agencies on the Public or Partner
                                Candidate Boards.
                              </li>
                              <li>
                                <strong>Partners Only</strong> - flagged for
                                Role Relay automatic matching, and listed on
                                the Partner Candidate Board. The candidate's
                                anonymous details will not be searchable on
                                the Public Candidate Board.
                              </li>
                              <li>
                                <strong>Public</strong> - flagged for
                                matching, and listed on the Partner and Public
                                Candidate Board.
                              </li>
                              <li>
                                <strong>Community</strong> - flagged for
                                matching, and listed on the Community
                                Candidate Board visibile to other community
                                members.
                              </li>
                            </ul>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <select
                      id="audience"
                      name="audience"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={fv.audience}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    >
                      <option value=""></option>
                      <option value="PRIVATE">Private</option>
                      <option value="PARTNER">Partners only</option>
                      <option value="COMMUNITY">Community only</option>
                      <option value="PUBLIC">
                        Public (incl. Partners & Communities)
                      </option>
                    </select>
                    <ErrorWrapper field="audience" />
                  </div> */}
                </div>
                <br />
                <div className="space-y-2">
                  {!loadingProjectBoards &&
                    !errorProjectBoards &&
                    dataProjectBoards && (
                      <>
                        <Label htmlFor="projectBoards">Projects</Label>
                        <ReactSelect
                          onChange={(data: any) => {
                            let candidateBoardIds = [];
                            for (let i in data)
                              candidateBoardIds.push(data[i]?.value);
                            formik.setFieldValue(
                              "projectCandidateBoardIds",
                              candidateBoardIds
                            );
                            setProjectBoardsValue(data);
                          }}
                          name="projectBoards"
                          isMulti={true}
                          options={projectBoards}
                          value={projectBoardsValue}
                        />
                      </>
                    )}
                </div>
              </form>

              {candidate && (
                <>
                  <hr />
                  <div>
                    <AttachmentSection
                      entityId={candidate.id}
                      type="candidate"
                      onUploadComplete={() => {
                        // Trigger refresh of attachment list
                      }}
                      onAttachmentDeleted={() => {
                        // Handle attachment deletion if needed
                      }}
                    />
                  </div>
                </>
              )}
              {/* <hr />
              <p>
                <strong>What happens next?</strong>
              </p>
              <p>
                Your Candidate will be shared with the Role Relay network based
                on the Audience you have selected. Only the anonymous details
                you have provided will be shared with other agencies.
              </p> */}
            </div>
          <DialogFooter>
            <ModalFooterWrapper>
              <ModalFooterLeft>
                {candidate && (
                  <Button
                    variant="link"
                    onClick={(e) => {
                      e.preventDefault();
                      onDelete();
                    }}
                  >
                    Remove this candidate from Role Relay
                  </Button>
                )}
              </ModalFooterLeft>
              <ModalFooterRight>
                <Button className="mx-1" variant="secondary" onClick={onHide}>
                  Cancel
                </Button>
                <Button
                  className="mx-1"
                  variant="default"
                  disabled={formik.isSubmitting}
                  onClick={() => {
                    formik.setFieldValue("share", sharing);
                    formik.submitForm();
                  }}
                >
                  Save
                </Button>
              </ModalFooterRight>
            </ModalFooterWrapper>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectCandidateDetailModal;
